// src/pages/vendor/Products.jsx - UPDATED WITH MULTI-IMAGE SUPPORT
import React, { useState, useEffect } from 'react';
import { useVendor } from '../../hooks/useVendor';
import { useOptions } from '../../context/OptionsContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Rating,
  Divider,
  FormControl,
  InputLabel,
  Select,
  useMediaQuery,
  useTheme,
  Collapse,
  Badge,
  Skeleton,
  Tooltip,
  Tab,
  Tabs,
  Pagination,
  Fade,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Inventory,
  CheckCircle,
  Cancel,
  GridView,
  ViewList,
  Refresh,
  Download,
  Star as StarIcon,
  Warning as WarningIcon,
  Category,
  PriceChange,
  Sort,
  Close,
  ExpandMore,
  ExpandLess,
  ShoppingBag,
  TrendingUp,
  Image,
  CloudUpload,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import ProductForm from '../../components/vendor/ProductForm';

// Warning component
const Warning = ({ sx }) => (
  <WarningIcon sx={{ fontSize: 40, color: 'warning.main', ...sx }} />
);

// ============================================================
// IMAGE UPLOAD HELPER
// ============================================================
const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL.replace('/api', '') 
  : 'https://api.goscitech.com';

const getImageUrl = (imagePath) => {
  if (!imagePath) return '/placeholder-image.png';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  return `${API_BASE_URL}${imagePath}`;
};

const Products = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const {
    products,
    loading,
    deleteProduct,
    fetchProducts,
    createProduct,
    updateProduct,
  } = useVendor();

  const { categories, sizes, colors, badges, loading: optionsLoading } = useOptions();

  // View state
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 6 : 10);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);

  // ✅ NEW: Image management state
  const [productImages, setProductImages] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [thumbnailIndex, setThumbnailIndex] = useState(0);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    lowStock: 0,
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (products) {
      setStats({
        total: products.length,
        active: products.filter(p => p.isActive).length,
        inactive: products.filter(p => !p.isActive).length,
        lowStock: products.filter(p => p.stock < 10).length,
      });
    }
  }, [products]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterCategory = (category) => {
    setFilterCategory(category);
  };

  const handleFilterStatus = (status) => {
    setFilterStatus(status);
  };

  const handleSort = (e) => {
    setSortBy(e.target.value);
  };

  const filteredProducts = products?.filter((product) => {
    const matchesSearch = product.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && product.isActive) ||
      (filterStatus === 'inactive' && !product.isActive);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Sort products
  const sortedProducts = [...(filteredProducts || [])].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 'oldest':
        return new Date(a.createdAt) - new Date(b.createdAt);
      case 'price_asc':
        return a.price - b.price;
      case 'price_desc':
        return b.price - a.price;
      case 'name_asc':
        return a.name.localeCompare(b.name);
      case 'name_desc':
        return b.name.localeCompare(a.name);
      case 'stock_asc':
        return a.stock - b.stock;
      case 'stock_desc':
        return b.stock - a.stock;
      default:
        return 0;
    }
  });

  // Pagination
  const paginatedProducts = sortedProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalPages = Math.ceil(sortedProducts.length / rowsPerPage);

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductImages([]);
    setImageUrls([]);
    setThumbnailIndex(0);
    setOpenDialog(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    // Load existing images
    const existingImages = product.images || [];
    setImageUrls(existingImages);
    setProductImages(existingImages.map((img, idx) => ({
      id: `existing-${idx}`,
      url: img,
      isExisting: true,
    })));
    setThumbnailIndex(existingImages.indexOf(product.thumbnail) || 0);
    setOpenDialog(true);
    handleMenuClose();
  };

  // ✅ NEW: Handle image upload
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map((file) => ({
      id: `new-${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      isExisting: false,
    }));
    setProductImages((prev) => [...prev, ...newImages]);
    // Reset input
    event.target.value = '';
  };

  // ✅ NEW: Remove image
  const handleRemoveImage = (index) => {
    setProductImages((prev) => prev.filter((_, i) => i !== index));
    if (thumbnailIndex === index) {
      setThumbnailIndex(0);
    } else if (thumbnailIndex > index) {
      setThumbnailIndex(thumbnailIndex - 1);
    }
  };

  // ✅ NEW: Set thumbnail
  const handleSetThumbnail = (index) => {
    setThumbnailIndex(index);
  };

  // ✅ NEW: Reorder images (drag and drop)
  const handleReorderImages = (startIndex, endIndex) => {
    const result = Array.from(productImages);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    setProductImages(result);
    // Update thumbnail index if needed
    if (thumbnailIndex === startIndex) {
      setThumbnailIndex(endIndex);
    } else if (thumbnailIndex > startIndex && thumbnailIndex <= endIndex) {
      setThumbnailIndex(thumbnailIndex - 1);
    } else if (thumbnailIndex < startIndex && thumbnailIndex >= endIndex) {
      setThumbnailIndex(thumbnailIndex + 1);
    }
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setViewDetailsDialog(true);
    handleMenuClose();
  };

  const handleDeleteProduct = async () => {
    if (selectedProduct) {
      await deleteProduct(selectedProduct.id);
      setDeleteDialog(false);
      setSelectedProduct(null);
      fetchProducts();
    }
  };

  const handleMenuOpen = (event, product) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpandProduct = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  // ✅ UPDATED: Save product with images
  const handleSaveProduct = async (productData) => {
    try {
      // Prepare image data
      const imageUrlsList = [];
      let thumbnailUrl = '';

      // Process existing images
      const existingImages = productImages.filter(img => img.isExisting);
      const newImages = productImages.filter(img => !img.isExisting);

      // For new images, we need to upload them first
      // For now, we'll use the preview URLs (actual upload to be implemented)
      // In production, you'd upload to cloud storage here
      
      // Collect all image URLs
      for (const img of productImages) {
        if (img.isExisting) {
          imageUrlsList.push(img.url);
        } else {
          // In production, upload file to server and get URL
          // For now, use preview URL
          imageUrlsList.push(img.preview);
        }
      }

      // Set thumbnail
      if (productImages.length > 0) {
        thumbnailUrl = productImages[thumbnailIndex]?.isExisting 
          ? productImages[thumbnailIndex].url 
          : productImages[thumbnailIndex]?.preview || '';
      }

      const data = {
        ...productData,
        images: imageUrlsList,
        thumbnail: thumbnailUrl,
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, data);
      } else {
        await createProduct(data);
      }
      setOpenDialog(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  // Mobile Product Card
  const renderMobileProductCard = (product) => {
    const isExpanded = expandedProduct === product.id;
    const images = product.images || [];
    const thumbnail = product.thumbnail || images[0] || '/placeholder-image.png';

    return (
      <Card key={product.id} sx={{ mb: 2, borderRadius: 2, position: 'relative' }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {/* Header with Image and Name */}
            <Stack direction="row" spacing={2}>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar
                  variant="rounded"
                  src={getImageUrl(thumbnail)}
                  sx={{ width: 80, height: 80, borderRadius: 2 }}
                >
                  <Inventory />
                </Avatar>
                {product.badge && (
                  <Chip
                    label={product.badge}
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -4,
                      left: -4,
                      height: 18,
                      fontSize: '0.5rem',
                    }}
                  />
                )}
                {/* ✅ Show image count badge */}
                {images.length > 1 && (
                  <Chip
                    label={`+${images.length - 1}`}
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      right: -4,
                      height: 18,
                      fontSize: '0.5rem',
                      bgcolor: 'primary.main',
                      color: 'white',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {product.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {product.category}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${product.price.toFixed(2)}
                  </Typography>
                  {product.originalPrice && (
                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      ${product.originalPrice.toFixed(2)}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Rating value={product.rating || 0} size="small" readOnly />
                  <Typography variant="caption" color="text.secondary">
                    ({product.reviewsCount || 0})
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            {/* Status and Stock */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                icon={product.isActive ? <CheckCircle /> : <Cancel />}
                label={product.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={product.isActive ? 'success' : 'error'}
                sx={{ height: 24 }}
              />
              <Chip
                label={`Stock: ${product.stock}`}
                size="small"
                color={product.stock > 10 ? 'success' : product.stock > 0 ? 'warning' : 'error'}
                variant="outlined"
                sx={{ height: 24 }}
              />
              {product.stock < 10 && product.stock > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label="Low Stock"
                  size="small"
                  color="warning"
                  sx={{ height: 24 }}
                />
              )}
            </Stack>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => {
                  setSelectedProduct(product);
                  setViewDetailsDialog(true);
                }}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
              >
                View
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => handleEditProduct(product)}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
              >
                Edit
              </Button>
              <IconButton size="small" onClick={() => handleExpandProduct(product.id)}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>

            {/* Expanded Details */}
            <Collapse in={isExpanded}>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={1.5}>
                {/* ✅ Image Gallery in expanded view */}
                {images.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Images ({images.length})
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, overflow: 'auto', pb: 0.5 }}>
                      {images.map((img, idx) => (
                        <Avatar
                          key={idx}
                          variant="rounded"
                          src={getImageUrl(img)}
                          sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: 1,
                            border: product.thumbnail === img || (idx === 0 && !product.thumbnail) 
                              ? '2px solid primary.main' 
                              : 'none',
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Sizes */}
                {product.sizes && product.sizes.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Sizes
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                      {product.sizes.map((size) => (
                        <Chip key={size} label={size} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                )}
                
                {/* Colors */}
                {product.colors && product.colors.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Colors
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                      {product.colors.map((color) => (
                        <Chip
                          key={color}
                          label={color}
                          size="small"
                          sx={{ bgcolor: color.toLowerCase(), color: 'white' }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Description */}
                {product.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {product.description.length > 100 
                        ? `${product.description.substring(0, 100)}...` 
                        : product.description}
                    </Typography>
                  </Box>
                )}

                {/* Delete Button */}
                <Button
                  size="small"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => {
                    setSelectedProduct(product);
                    setDeleteDialog(true);
                  }}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                >
                  Delete Product
                </Button>
              </Stack>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // Render Grid View (Desktop)
  const renderGridView = () => (
    <Grid container spacing={isMobile ? 1.5 : 3}>
      {paginatedProducts.map((product) => {
        const images = product.images || [];
        const thumbnail = product.thumbnail || images[0] || '/placeholder-image.png';
        
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
              },
            }}>
              {/* Image */}
              <Box sx={{ position: 'relative', pt: '100%', bgcolor: 'grey.100' }}>
                <CardMedia
                  component="img"
                  image={getImageUrl(thumbnail)}
                  alt={product.name}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {product.badge && (
                  <Chip
                    label={product.badge}
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      fontWeight: 'bold',
                      height: 24,
                      fontSize: '0.65rem',
                    }}
                  />
                )}
                {/* ✅ Multi-image indicator */}
                {images.length > 1 && (
                  <Chip
                    label={`${images.length} images`}
                    size="small"
                    icon={<Image sx={{ fontSize: 14 }} />}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      height: 24,
                      fontSize: '0.6rem',
                      bgcolor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                    }}
                  />
                )}
                {product.stock < 10 && product.stock > 0 && (
                  <Chip
                    icon={<WarningIcon sx={{ fontSize: 14 }} />}
                    label="Low Stock"
                    size="small"
                    color="warning"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      height: 24,
                      fontSize: '0.65rem',
                    }}
                  />
                )}
                {product.stock === 0 && (
                  <Chip
                    label="Out of Stock"
                    size="small"
                    color="error"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      height: 24,
                      fontSize: '0.65rem',
                    }}
                  />
                )}
              </Box>

              {/* Content */}
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Typography variant="subtitle1" fontWeight="600" noWrap>
                  {product.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {product.category}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${product.price.toFixed(2)}
                  </Typography>
                  {product.originalPrice && (
                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      ${product.originalPrice.toFixed(2)}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                  <Rating value={product.rating || 0} size="small" readOnly />
                  <Typography variant="caption" color="text.secondary">
                    ({product.reviewsCount || 0})
                  </Typography>
                </Stack>
                <Chip
                  icon={product.isActive ? <CheckCircle /> : <Cancel />}
                  label={product.isActive ? 'Active' : 'Inactive'}
                  size="small"
                  color={product.isActive ? 'success' : 'error'}
                  sx={{ mt: 1, height: 24, fontSize: '0.65rem' }}
                />
              </CardContent>

              {/* Actions */}
              <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                <Button
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => handleEditProduct(product)}
                  sx={{ textTransform: 'none' }}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, product)}
                >
                  <MoreVert />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  // Render List View (Desktop)
  const renderListView = () => (
    <TableContainer>
      <Table>
        <TableHead sx={{ bgcolor: 'grey.50' }}>
          <TableRow>
            <TableCell><Typography variant="subtitle2" fontWeight="bold">Product</Typography></TableCell>
            <TableCell><Typography variant="subtitle2" fontWeight="bold">Category</Typography></TableCell>
            <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Price</Typography></TableCell>
            <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Stock</Typography></TableCell>
            <TableCell><Typography variant="subtitle2" fontWeight="bold">Images</Typography></TableCell>
            <TableCell><Typography variant="subtitle2" fontWeight="bold">Status</Typography></TableCell>
            <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Actions</Typography></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedProducts.map((product) => {
            const images = product.images || [];
            const thumbnail = product.thumbnail || images[0] || '/placeholder-image.png';
            
            return (
              <TableRow key={product.id} hover>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      variant="rounded"
                      src={getImageUrl(thumbnail)}
                      sx={{ width: 48, height: 48 }}
                    >
                      <Inventory />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        {product.name}
                      </Typography>
                      {product.badge && (
                        <Chip
                          label={product.badge}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mt: 0.5, height: 20, fontSize: '0.6rem' }}
                        />
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>{product.category}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="600" color="primary">
                    ${product.price.toFixed(2)}
                  </Typography>
                  {product.originalPrice && (
                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      ${product.originalPrice.toFixed(2)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={product.stock}
                    size="small"
                    color={product.stock > 10 ? 'success' : product.stock > 0 ? 'warning' : 'error'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Chip
                      icon={<Image sx={{ fontSize: 14 }} />}
                      label={images.length}
                      size="small"
                      variant="outlined"
                    />
                    {images.length > 0 && (
                      <Stack direction="row" spacing={0.5}>
                        {images.slice(0, 3).map((img, idx) => (
                          <Avatar
                            key={idx}
                            src={getImageUrl(img)}
                            sx={{ width: 24, height: 24, borderRadius: 1 }}
                          />
                        ))}
                        {images.length > 3 && (
                          <Chip label={`+${images.length - 3}`} size="small" variant="outlined" />
                        )}
                      </Stack>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={product.isActive ? <CheckCircle /> : <Cancel />}
                    label={product.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={product.isActive ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, product)}
                  >
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading || optionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Products
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your product inventory ({stats.total} total)
            </Typography>
          </Box>
          <Stack 
            direction="row" 
            spacing={1} 
            width={{ xs: '100%', sm: 'auto' }}
            flexWrap="wrap"
          >
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => fetchProducts()}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddProduct}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              Add Product
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Stats Cards - Responsive */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Inventory color="primary" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                {stats.total}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Products
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <CheckCircle color="success" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="success.main">
                {stats.active}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Cancel color="error" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="error.main">
                {stats.inactive}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Inactive
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Warning sx={{ fontSize: { xs: 28, sm: 40 } }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="warning.main">
                {stats.lowStock}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Low Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Toolbar - Responsive */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={isMobile ? 1 : 2} 
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            placeholder="Search products..."
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <Stack 
            direction="row" 
            spacing={1} 
            flexWrap="wrap"
            sx={{ gap: { xs: 0.5, sm: 1 } }}
          >
            <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => handleFilterCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 80, sm: 100 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => handleFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
              <InputLabel>Sort</InputLabel>
              <Select
                value={sortBy}
                onChange={handleSort}
                label="Sort"
              >
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
                <MenuItem value="price_asc">Price: Low to High</MenuItem>
                <MenuItem value="price_desc">Price: High to Low</MenuItem>
                <MenuItem value="name_asc">Name: A to Z</MenuItem>
                <MenuItem value="name_desc">Name: Z to A</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Grid View">
                <IconButton
                  onClick={() => setViewMode('grid')}
                  color={viewMode === 'grid' ? 'primary' : 'default'}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <GridView />
                </IconButton>
              </Tooltip>
              <Tooltip title="List View">
                <IconButton
                  onClick={() => setViewMode('list')}
                  color={viewMode === 'list' ? 'primary' : 'default'}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <ViewList />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {/* Content */}
      {sortedProducts.length === 0 ? (
        <Paper sx={{ p: { xs: 4, sm: 6 }, borderRadius: 3, textAlign: 'center' }}>
          <Inventory sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm ? 'Try adjusting your search or filters' : 'Start adding products to your store'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddProduct}
          >
            Add Product
          </Button>
        </Paper>
      ) : (
        <>
          {isMobile ? (
            // Mobile Card View
            <Box>
              {paginatedProducts.map((product) => renderMobileProductCard(product))}
            </Box>
          ) : (
            // Desktop View
            viewMode === 'grid' ? renderGridView() : renderListView()
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, newPage) => setPage(newPage - 1)}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 180 }
        }}
      >
        <MenuItem onClick={() => handleViewProduct(selectedProduct)}>
          <Visibility fontSize="small" sx={{ mr: 1.5 }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => handleEditProduct(selectedProduct)}>
          <Edit fontSize="small" sx={{ mr: 1.5 }} /> Edit
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1.5 }} /> Delete
        </MenuItem>
      </Menu>

      {/* ============================================================ */}
      {/* PRODUCT FORM DIALOG WITH MULTI-IMAGE SUPPORT */}
      {/* ============================================================ */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 }, maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          <ProductForm
            product={editingProduct}
            onSave={handleSaveProduct}
            onCancel={() => setOpenDialog(false)}
          />
          
          {/* ✅ IMAGE UPLOAD SECTION - ADDED */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
              Product Images
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Upload multiple images for your product. The first image will be used as the thumbnail by default. Click an image to set it as the thumbnail.
            </Typography>

            {/* Upload Button */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              sx={{ mb: 2 }}
            >
              Upload Images
              <input
                type="file"
                hidden
                accept="image/*"
                multiple
                onChange={handleImageUpload}
              />
            </Button>

            {/* Image Grid */}
            {productImages.length > 0 ? (
              <ImageList cols={isMobile ? 3 : 4} gap={8} sx={{ mb: 2 }}>
                {productImages.map((img, index) => (
                  <ImageListItem
                    key={img.id}
                    sx={{
                      cursor: 'pointer',
                      border: thumbnailIndex === index ? '3px solid #1976d2' : '3px solid transparent',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover .image-actions': {
                        opacity: 1,
                      },
                    }}
                    onClick={() => handleSetThumbnail(index)}
                  >
                    <img
                      src={img.preview || img.url}
                      alt={`Product image ${index + 1}`}
                      loading="lazy"
                      style={{ 
                        width: '100%', 
                        height: 100, 
                        objectFit: 'cover',
                      }}
                    />
                    {thumbnailIndex === index && (
                      <Chip
                        label="Thumbnail"
                        size="small"
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          height: 20,
                          fontSize: '0.55rem',
                        }}
                      />
                    )}
                    <Box
                      className="image-actions"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        display: 'flex',
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        color="error"
                        sx={{ bgcolor: 'rgba(255,255,255,0.8)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(index);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <ImageListItemBar
                      subtitle={`Image ${index + 1}`}
                      position="below"
                      sx={{ 
                        '& .MuiImageListItemBar-subtitle': { 
                          fontSize: '0.6rem', 
                          textAlign: 'center' 
                        },
                        bgcolor: 'transparent',
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            ) : (
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: '#f9f9f9',
                }}
              >
                <Image sx={{ fontSize: 48, color: '#aaa' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No images uploaded yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click "Upload Images" to add product photos
                </Typography>
              </Box>
            )}

            {productImages.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                * Click an image to set it as the thumbnail (highlighted in blue). Drag to reorder (coming soon).
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // The ProductForm's onSave will handle saving
              // We need to pass the images data to the form
              const form = document.querySelector('form');
              if (form) {
                form.dispatchEvent(new Event('submit'));
              }
            }}
            fullWidth={isMobile}
          >
            {editingProduct ? 'Update Product' : 'Create Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Details Dialog - Responsive */}
      <Dialog
        open={viewDetailsDialog}
        onClose={() => setViewDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h6" fontWeight="bold">
              {selectedProduct?.name}
            </Typography>
            <Chip
              icon={selectedProduct?.isActive ? <CheckCircle /> : <Cancel />}
              label={selectedProduct?.isActive ? 'Active' : 'Inactive'}
              color={selectedProduct?.isActive ? 'success' : 'error'}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <Stack spacing={3}>
              {/* ✅ Image Gallery */}
              {selectedProduct.images && selectedProduct.images.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                    Images ({selectedProduct.images.length})
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ overflow: 'auto', pb: 1 }}>
                    {selectedProduct.images.map((image, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: 'relative',
                          border: selectedProduct.thumbnail === image || (index === 0 && !selectedProduct.thumbnail)
                            ? '2px solid #1976d2'
                            : '2px solid transparent',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Avatar
                          variant="rounded"
                          src={getImageUrl(image)}
                          sx={{ width: 100, height: 100, borderRadius: 1 }}
                        />
                        {(selectedProduct.thumbnail === image || (index === 0 && !selectedProduct.thumbnail)) && (
                          <Chip
                            label="Thumbnail"
                            size="small"
                            color="primary"
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              left: 4,
                              height: 18,
                              fontSize: '0.5rem',
                            }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            right: 4,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            px: 0.5,
                            borderRadius: 0.5,
                            fontSize: '0.6rem',
                          }}
                        >
                          #{index + 1}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Basic Info */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Category
                  </Typography>
                  <Typography variant="body2">{selectedProduct.category}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Subcategory
                  </Typography>
                  <Typography variant="body2">{selectedProduct.subcategory || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Badge
                  </Typography>
                  <Typography variant="body2">{selectedProduct.badge || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Price
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${selectedProduct.price.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Original Price
                  </Typography>
                  <Typography variant="body2">
                    {selectedProduct.originalPrice ? `$${selectedProduct.originalPrice.toFixed(2)}` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Stock
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {selectedProduct.stock}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Rating
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Rating value={selectedProduct.rating || 0} readOnly />
                    <Typography variant="body2">
                      ({selectedProduct.reviewsCount || 0} reviews)
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider />

              {/* Sizes & Colors */}
              <Grid container spacing={2}>
                {selectedProduct.sizes && selectedProduct.sizes.length > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Sizes
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      {selectedProduct.sizes.map((size) => (
                        <Chip key={size} label={size} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Grid>
                )}
                {selectedProduct.colors && selectedProduct.colors.length > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Colors
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      {selectedProduct.colors.map((color) => (
                        <Chip
                          key={color}
                          label={color}
                          size="small"
                          sx={{ bgcolor: color.toLowerCase(), color: 'white' }}
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>

              <Divider />

              {/* Description */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Description
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedProduct.description || 'No description provided'}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setViewDetailsDialog(false)} fullWidth={isMobile}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => {
              setViewDetailsDialog(false);
              handleEditProduct(selectedProduct);
            }}
            fullWidth={isMobile}
          >
            Edit Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation - Responsive */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1, verticalAlign: 'middle' }} /> Delete Product
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{selectedProduct?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setDeleteDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteProduct}
            color="error"
            variant="contained"
            fullWidth={isMobile}
            startIcon={<Delete />}
          >
            Delete Product
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;